import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import { useQueryClient, useQuery } from "react-query";
import { QK } from "../../../base/qk";
import { getAllergyTypes, createAllergy } from "../../../api/services/allergy";
import { useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import CustomSelect from "../../FormFields/CustomSelect/CustomSelect";

const AllergiesModal = ({ show, onHide }) => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const { id: clientId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const { data: allergyOptions } = useQuery([QK.allergyTypes], () =>
    getAllergyTypes()
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.allergy_type) {
      enqueueSnackbar("Please select an allergy type", { variant: "warning" });
      return;
    }

    setIsLoading(true);
    try {
      const updatedFormData = { ...formData, client: clientId };
      const response = await createAllergy(updatedFormData);
      if (response.status === 201) {
        enqueueSnackbar("Allergy added successfully", { variant: "success" });
        setFormData({});
        queryClient.invalidateQueries(QK.allergies);
        onHide();
      }
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || "Failed to add allergy", {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({});
    onHide();
  };

  // Transform allergy options for CustomSelect
  const allergyOptionsForSelect = allergyOptions?.map((option) => ({
    label: option.name || option.label || option,
    value: option.id || option.value || option,
  })) || [];

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflowY: "auto",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: "#EFF8FF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MedicalServicesIcon sx={{ color: "#2469F0", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                Add Allergy
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                Record a new allergy for the client
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.4 }} />
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, pt: 2, overflowY: "visible" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box sx={{ width: "100%" }}>
            <CustomSelect
              label="Allergy Type"
              name="allergy_type"
              value={formData?.allergy_type || ""}
              onChange={handleChange}
              options={allergyOptionsForSelect}
              placeholder="Select allergy type"
              fullWidth
              size="medium"
              sx={{
                "& .MuiInputLabel-root": {
                  whiteSpace: "nowrap",
                  overflow: "visible",
                  textOverflow: "clip",
                  maxWidth: "100%",
                  position: "relative",
                  transform: "none",
                  marginBottom: "6px",
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  color: "#64748b",
                },
                "& .MuiFormControl-root": {
                  width: "100%",
                },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  "&:hover fieldset": {
                    borderColor: "#2469F0",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2469F0",
                    borderWidth: "2px",
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ width: "100%" }}>
            <TextField
              label="Comment"
              name="comment"
              multiline
              rows={3}
              fullWidth
              value={formData?.comment || ""}
              onChange={handleChange}
              placeholder="Add any additional notes about the allergy..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  "&:hover fieldset": {
                    borderColor: "#2469F0",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2469F0",
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  color: "#64748b",
                  "&.Mui-focused": {
                    color: "#2469F0",
                  },
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={isLoading}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderRadius: "10px",
            borderColor: "#D0D5DD",
            color: "#344054",
            "&:hover": {
              bgcolor: "#F9FAFB",
              borderColor: "#98A2B3",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            bgcolor: "#2469F0",
            px: 3,
            "&:hover": {
              bgcolor: "#1A5BD4",
              boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
            },
          }}
        >
          {isLoading ? "Saving..." : "Save Allergy"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AllergiesModal;